import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import apiClient from '@/lib/api-client';

import {
    Tag, Plus, Search, X, Save, Trash2, Package,
    Calendar, Percent, Clock, CheckCircle, Ban,
    RefreshCw, ArrowLeft, Store,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

export function ClearancePage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [form, setForm] = useState({
        name: '', description: '', discountPercentage: '',
        startDate: '', endDate: '', productIds: [] as string[], clearancePrice: '',
    });

    const { data: campaigns, isLoading, refetch } = useQuery({
        queryKey: ['clearance-campaigns', user?.tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/clearance-campaigns`);
            return data?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    const { data: products } = useQuery({
        queryKey: ['products-clearance', user?.tenantId, productSearch],
        queryFn: async () => {
            const params: any = { limit: 50, sortBy: 'name', order: 'asc' };
            if (productSearch) params.search = productSearch;
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products`, { params });
            return data?.data || [];
        },
        enabled: !!user?.tenantId,
    });
    const createMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post(`/tenants/${user!.tenantId}/clearance-campaigns`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['clearance-campaigns'] });
            setShowForm(false);
            setForm({ name: '', description: '', discountPercentage: '', startDate: '', endDate: '', productIds: [], clearancePrice: '' });
            addToast({ type: 'success', title: 'Campaign created' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    const toggleCampaignMutation = useMutation({
        mutationFn: ({ campaignId, isActive }: { campaignId: string; isActive: boolean }) =>
            apiClient.patch(`/tenants/${user!.tenantId}/clearance-campaigns/${campaignId}`, { isActive }),
        onSuccess: (_, vars) => {
            queryClient.invalidateQueries({ queryKey: ['clearance-campaigns'] });
            addToast({ type: 'success', title: vars.isActive ? 'Campaign Activated' : 'Campaign Ended' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Update failed', message: err?.response?.data?.error?.message }),
    });

    const deleteCampaignMutation = useMutation({
        mutationFn: (campaignId: string) =>
            apiClient.delete(`/tenants/${user!.tenantId}/clearance-campaigns/${campaignId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['clearance-campaigns'] });
            addToast({ type: 'success', title: 'Campaign removed' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Delete failed', message: err?.response?.data?.error?.message }),
    });

    const campaignsList = (campaigns || []) as any[];
    const productsList = (products || []) as any[];

    const toggleProduct = (productId: string) => {
        setForm(prev => ({
            ...prev,
            productIds: prev.productIds.includes(productId)
                ? prev.productIds.filter(id => id !== productId)
                : [...prev.productIds, productId],
        }));
    };

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString()}`;
    const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    const activeCampaigns = campaignsList.filter((c: any) => c.isActive).length;
    const now = new Date();
    const currentlyRunning = campaignsList.filter((c: any) => {
        if (!c.isActive) return false;
        const start = new Date(c.startDate);
        const end = new Date(c.endDate);
        return now >= start && now <= end;
    }).length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-lg shadow-rose-500/25">
                                <Tag className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Clearance Campaigns</h1>
                                <p className="text-sm text-slate-500">
                                    {campaignsList.length} campaigns · {activeCampaigns} active · {currentlyRunning} running now
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => refetch()} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm" type="button">
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                            <button onClick={() => { setShowForm(true); }}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all font-semibold shadow-lg shadow-rose-500/25 text-sm" type="button">
                                <Plus className="w-4 h-4" /> New Campaign
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <QuickStat label="Total Campaigns" value={campaignsList.length} icon={Tag} gradient="from-rose-500 to-pink-600" />
                    <QuickStat label="Active" value={activeCampaigns} icon={CheckCircle} gradient="from-emerald-500 to-teal-600" />
                    <QuickStat label="Running Now" value={currentlyRunning} icon={Clock} gradient="from-amber-500 to-orange-600" pulse={currentlyRunning > 0} />
                    <QuickStat label="Products on Sale" value={campaignsList.reduce((s: number, c: any) => s + (c.products?.length || 0), 0)} icon={Package} gradient="from-blue-500 to-indigo-600" />
                </div>

                {/* Campaigns List */}
                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : campaignsList.length === 0 ? (
                    <EmptyState
                        icon={<Tag size={48} />}
                        title="No clearance campaigns"
                        description="Create clearance campaigns to discount products for a specific period."
                        action={{ label: 'Create Campaign', onClick: () => setShowForm(true) }}
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {campaignsList.map((campaign: any) => {
                            const isRunning = campaign.isActive && new Date(campaign.startDate) <= now && new Date(campaign.endDate) >= now;
                            const isExpired = new Date(campaign.endDate) < now;
                            const isUpcoming = new Date(campaign.startDate) > now;

                            return (
                                <motion.div key={campaign.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                                    className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:shadow-lg p-5 transition-all">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shadow-md',
                                                isRunning ? 'bg-gradient-to-br from-emerald-500 to-teal-600' :
                                                    isUpcoming ? 'bg-gradient-to-br from-blue-500 to-indigo-600' :
                                                        'bg-gradient-to-br from-slate-400 to-slate-500')}>
                                                <Percent size={20} className="text-white" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                                                <p className="text-xs text-slate-400">{campaign.products?.length || 0} products</p>
                                            </div>
                                        </div>
                                        <Badge variant={isRunning ? 'success' : isUpcoming ? 'info' : isExpired ? 'danger' : 'neutral'} size="sm">
                                            {isRunning ? 'Running' : isUpcoming ? 'Upcoming' : isExpired ? 'Expired' : 'Inactive'}
                                        </Badge>
                                    </div>

                                    {campaign.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{campaign.description}</p>}

                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                        <div><p className="text-slate-400">Discount</p><p className="font-bold text-rose-600">{campaign.discountPercentage}%</p></div>
                                        <div><p className="text-slate-400">Start</p><p className="font-medium">{formatDate(campaign.startDate)}</p></div>
                                        <div><p className="text-slate-400">End</p><p className="font-medium">{formatDate(campaign.endDate)}</p></div>
                                    </div>

                                    {/* Product Preview */}
                                    <div className="mt-3 pt-3 border-t border-slate-100">
                                        <div className="flex flex-wrap gap-1">
                                            {campaign.products?.slice(0, 4).map((cp: any) => (
                                                <span key={cp.id} className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded text-[10px] font-medium">
                                                    {cp.productName || 'Product'}
                                                </span>
                                            ))}
                                            {campaign.products?.length > 4 && (
                                                <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px]">+{campaign.products.length - 4}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Controls */}
                                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                        <button
                                            type="button"
                                            onClick={() => toggleCampaignMutation.mutate({ campaignId: campaign.id, isActive: !campaign.isActive })}
                                            className={clsx(
                                                'text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors',
                                                campaign.isActive
                                                    ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
                                                    : 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                                            )}
                                        >
                                            {campaign.isActive ? 'End Campaign' : 'Reactivate'}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (window.confirm(`Delete clearance campaign "${campaign.name}"?`)) {
                                                    deleteCampaignMutation.mutate(campaign.id);
                                                }
                                            }}
                                            className="text-xs font-semibold px-2 py-1 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex items-center gap-1"
                                        >
                                            <Trash2 size={13} />
                                            Delete
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Create Campaign Modal */}
                <AnimatePresence>
                    {showForm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                                <div className="bg-gradient-to-r from-rose-500 to-pink-600 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
                                    <div className="flex items-center gap-3">
                                        <div className="p-1.5 rounded-lg bg-white/20"><Tag size={18} /></div>
                                        <div><h2 className="text-lg font-bold">Create Clearance Campaign</h2><p className="text-rose-100 text-xs">Set up a timed discount campaign</p></div>
                                    </div>
                                    <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30" type="button"><X size={16} /></button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Input label="Campaign Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., End of Season Sale" required />
                                        <Input label="Discount % *" type="number" step="0.01" value={form.discountPercentage} onChange={(e) => setForm({ ...form, discountPercentage: e.target.value })} placeholder="e.g., 25" required />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
                                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Input label="Start Date *" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
                                        <Input label="End Date *" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
                                    </div>
                                    <Input label="Clearance Price Override (optional)" type="number" step="0.01" value={form.clearancePrice} onChange={(e) => setForm({ ...form, clearancePrice: e.target.value })} placeholder="Leave empty to use discount %" />

                                    {/* Product Selection */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Products * ({form.productIds.length} selected)</label>
                                        </div>
                                        <div className="relative mb-2">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input type="text" placeholder="Search products..." value={productSearch}
                                                onChange={(e) => setProductSearch(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm" />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto border rounded-xl divide-y">
                                            {productsList.slice(0, 20).map((p: any) => (
                                                <label key={p.id} className={clsx(
                                                    'flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors',
                                                    form.productIds.includes(p.id) && 'bg-rose-50'
                                                )}>
                                                    <input type="checkbox" checked={form.productIds.includes(p.id)} onChange={() => toggleProduct(p.id)}
                                                        className="w-4 h-4 rounded border-slate-300 text-rose-600" />
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium">{p.name}</p>
                                                        <p className="text-xs text-slate-400">{p.sku || p.brand}</p>
                                                    </div>
                                                    <span className="text-sm font-semibold">{formatNaira(p.retailPrice)}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-3 flex-shrink-0">
                                    <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
                                    <Button onClick={() => createMutation.mutate(form)} isLoading={createMutation.isPending}
                                        disabled={!form.name || !form.discountPercentage || !form.startDate || !form.endDate || form.productIds.length === 0}
                                        leftIcon={<Save size={16} />}>
                                        Create Campaign
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

function QuickStat({ label, value, icon: Icon, gradient, pulse }: { label: string; value: number; icon: React.ElementType; gradient: string; pulse?: boolean }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className={clsx('text-2xl font-bold mt-1', pulse ? 'text-amber-600 animate-pulse' : 'text-slate-900')}>{value.toLocaleString()}</p>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}><Icon className="w-5 h-5 text-white" /></div>
            </div>
        </motion.div>
    );
}